import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { isFinalReq, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { ITransferTerm } from "./transferTermsStore";
import type { TFetchStatus, TParamsPagination } from "./types";

// Get all transferees (paginated)
export const fetchTransferees = createAsyncThunk(
    "transferees/fetchTransferees",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee`, {
                params: options,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get transferees data for autocomplete component:
export const fetchTransfereesAutocomplete = createAsyncThunk(
    "transferees/fetchTransfereesAutocomplete",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee`, {
                params: options,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get one transferee
export const fetchTransferee = createAsyncThunk(
    "transferees/fetchTransferee",
    async ({ transfereeId }: { transfereeId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee/${transfereeId}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Create one transferee
export const createTransferee = createAsyncThunk(
    "transferees/createTransferee",
    async (
        {
            data,
        }: { data: Omit<ITransferee, "id" | "createdAt" | "modifiedAt" | "companyId" | "transferTermCount" | "tags"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee/`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Update transferee
export const updateTransferee = createAsyncThunk(
    "transferees/updateTransferee",
    async (
        {
            transfereeId,
            data,
        }: {
            transfereeId: number;
            data: Omit<ITransferee, "id" | "createdAt" | "modifiedAt" | "companyId" | "transferTermCount" | "tags">;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transferee/${transfereeId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Delete transferee
export const deleteTransferee = createAsyncThunk(
    "transferees/eleteTransferee",
    async ({ transfereeId }: { transfereeId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee/${transfereeId}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get tags of a transferee
export const fetchTransfereesTags = createAsyncThunk(
    "transferees/fetchTransfereesTags",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee-tag`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Create a tag for a transferee
export const createTransfereeTag = createAsyncThunk(
    "transferees/createTransfereeTag",
    async ({ data }: { data: { transfereeId: number; name: string; color: number } }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee-tag`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Delete a transferee tag
export const deleteTransfereeTag = createAsyncThunk(
    "transferees/deleteTransfereeTag",
    async ({ tagId }: { tagId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/transferee-tag/${tagId}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get terms of a transferee
export const fetchTransfereeTerms = createAsyncThunk(
    "transferees/fetchTransfereeTerms",
    async ({ options, transfereeId }: { options: TParamsPagination; transfereeId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transferee/${transfereeId}/terms`,
                {
                    params: options,
                },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export interface ITransfereeTag {
    color: number;
    companyId: number;
    createdAt: Date | null;
    id: number;
    modifiedAt: Date | null;
    name: string;
    slug: string;
    transfereeId: number;
}

export interface ITransferee {
    id: number;
    createdAt: string;
    modifiedAt: string;
    name: string;
    avatarUrl: string;
    city: string;
    country: string;
    email: string;
    intraCommunityVat: string;
    siretNumber: string;
    street: string;
    zipCode: string;
    companyId: number;
    transferTermCount: null | number;
    tags: ITransfereeTag[];
}

interface InitialState {
    transferee: ITransferee;
    transferees: ITransferee[];
    transfereesCount: number;
    transfereesStatus: TFetchStatus;
    fetchTransfereesReqId: string;
    isListRefreshing: boolean;
    tagsStatus: TFetchStatus;
    transfereesTags: ITransfereeTag[];
    lastAddedTagId: number;
    transfereeTerms: ITransferTerm[];
    transfereeTermsCount: number;
    transfereeTermsStatus: TFetchStatus;
    transfereesAutocomplete: ITransferee[];
    createTransfereeStatus: TFetchStatus;
}

const initialState: InitialState = {
    transferee: {} as ITransferee,
    transferees: [],
    transfereesCount: 0,
    transfereesStatus: "idle",
    fetchTransfereesReqId: "",
    isListRefreshing: false,
    tagsStatus: "idle",
    transfereesTags: [],
    lastAddedTagId: -1,
    transfereeTerms: [],
    transfereeTermsCount: 0,
    transfereeTermsStatus: "idle",
    transfereesAutocomplete: [],
    createTransfereeStatus: "idle",
};

export const transfereesSlice = createSlice({
    name: "transferees",
    initialState,
    reducers: {
        setCurrentTransferee: (state, { payload }) => {
            if (state.transferee?.id !== payload?.id) {
                state.transfereeTerms = [];
                state.transfereeTermsCount = 0;
                state.transfereeTermsStatus = "idle";
            }
            state.transferee = payload;
        },
        resetTransferees(state) {
            state.transferees = [];
            state.transfereesCount = 0;
            state.transfereesStatus = "idle";
            state.fetchTransfereesReqId = "";
            state.isListRefreshing = false;
            state.transferee = {} as ITransferee;
            state.transfereeTerms = [];
            state.transfereeTermsCount = 0;
            state.transfereeTermsStatus = "idle";
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTransferees.fulfilled, (state, { payload, meta }) => {
            if (!isFinalReq(state.fetchTransfereesReqId, meta.requestId)) return;
            state.transferees = payload.entities;
            state.transfereesCount = payload.count;
            state.transfereesStatus = "succeeded";
            state.isListRefreshing = false;
        });
        builder.addCase(fetchTransferees.pending, (state, action) => {
            state.fetchTransfereesReqId = action.meta.requestId;
            state.transfereesStatus = "pending";
            if (!action.meta.arg?.options?.offset) {
                state.isListRefreshing = true;
            }
        });
        builder.addCase(fetchTransferees.rejected, (state, { payload, meta }) => {
            if (!isFinalReq(state.fetchTransfereesReqId, meta.requestId)) return;
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.transfereesStatus = "failed";
            state.isListRefreshing = false;
        });
        builder.addCase(fetchTransfereesAutocomplete.fulfilled, (state, { payload }) => {
            const transferees = payload.entities;
            if (payload.offset) {
                state.transfereesAutocomplete = [...state.transfereesAutocomplete].concat(transferees);
            } else {
                state.transfereesAutocomplete = transferees;
            }
        });
        builder.addCase(fetchTransfereesAutocomplete.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTransferee.fulfilled, (state, { payload }) => {
            state.transferee = payload;
        });
        builder.addCase(fetchTransferee.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(createTransferee.pending, (state) => {
            state.createTransfereeStatus = "pending";
        });
        builder.addCase(createTransferee.fulfilled, (state, { payload }) => {
            state.transferees = [...state.transferees, payload];
            state.transferee = payload;
            state.createTransfereeStatus = "succeeded";
            toast.success(`${t("Transferees.CreateSuccess")}`);
        });
        builder.addCase(createTransferee.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.createTransfereeStatus = "failed";
        });
        builder.addCase(updateTransferee.fulfilled, (state, { payload }) => {
            state.transferee = payload;
            state.transferees = state.transferees.map((transferee) => {
                if (transferee.id === payload.id) {
                    return payload;
                } else {
                    return transferee;
                }
            });
            toast.success(`${t("Transferees.EditSuccess")}`);
        });
        builder.addCase(updateTransferee.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteTransferee.fulfilled, (state, action) => {
            state.transferees = state.transferees.filter(
                (transferee) => transferee.id !== action.meta.arg.transfereeId,
            );
            toast.success(`${t("Transferees.DeleteSuccess")}`);
        });
        builder.addCase(deleteTransferee.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTransfereesTags.fulfilled, (state, { payload }) => {
            state.transfereesTags = payload;
            state.tagsStatus = "succeeded";
        });
        builder.addCase(fetchTransfereesTags.rejected, (state, { payload }) => {
            state.tagsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(createTransfereeTag.fulfilled, (state, { payload }) => {
            state.transfereesTags = [...state.transfereesTags, payload];
            state.lastAddedTagId = payload.id;

            const updatedTransferees = [...state.transferees].map((item) => {
                if (item.id === payload.transfereeId) {
                    return { ...item, tags: [...item.tags, payload] };
                } else {
                    return item;
                }
            });
            state.transferees = updatedTransferees;

            const currentTransferee = updatedTransferees.find((item) => item.id === payload.transfereeId);
            if (currentTransferee) {
                state.transferee = currentTransferee;
            }
        });
        builder.addCase(createTransfereeTag.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteTransfereeTag.fulfilled, (state, action) => {
            const tagId = action.meta.arg.tagId;

            // 1. Update tags
            const currentTag = [...state.transfereesTags].find((tag) => tag.id === tagId);
            const updatedTags = [...state.transfereesTags].filter((tag) => tag.id !== tagId);
            state.transfereesTags = updatedTags;

            // 2. Update transferees
            if (currentTag) {
                const updatedTransferees = [...state.transferees].map((item) => {
                    if (item.id === currentTag.transfereeId) {
                        return { ...item, tags: [...item.tags].filter((tag) => tag.id !== tagId) };
                    } else {
                        return item;
                    }
                });

                state.transferees = updatedTransferees;

                // 3. Update transferee
                const currentTransferee = updatedTransferees.find((item) => item.id === currentTag.transfereeId);
                if (currentTransferee) {
                    const updatedTransferee = {
                        ...currentTransferee,
                        tags: [...currentTransferee.tags].filter((tag) => tag.id !== tagId),
                    };
                    state.transferee = updatedTransferee;
                }
            }
        });
        builder.addCase(deleteTransfereeTag.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTransfereeTerms.fulfilled, (state, { payload, meta }) => {
            if (state.transferee?.id != null && state.transferee.id !== meta.arg.transfereeId) return;
            state.transfereeTerms = payload.entities;
            state.transfereeTermsCount = payload.count;
            state.transfereeTermsStatus = "succeeded";
        });
        builder.addCase(fetchTransfereeTerms.pending, (state, { meta }) => {
            if (state.transferee?.id != null && state.transferee.id !== meta.arg.transfereeId) return;
            state.transfereeTermsStatus = "pending";
        });
        builder.addCase(fetchTransfereeTerms.rejected, (state, { payload, meta }) => {
            if (state.transferee?.id != null && state.transferee.id !== meta.arg.transfereeId) return;
            state.transfereeTermsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default transfereesSlice.reducer;

export const { setCurrentTransferee, resetTransferees } = transfereesSlice.actions;
